import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { useRef, useState } from "react";
import type { CSSProperties, MouseEvent, ReactNode } from "react";

import { LanguageMenu } from "@/components/language-menu";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

export type LandingMenuLink = {
  label: string;
  to: string;
  hash?: string;
};

const roundButton =
  "grid size-10 shrink-0 place-items-center rounded-full border border-border bg-background outline-none transition-[background-color,transform] hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.96]";

/**
 * A public page's navigation on a phone: a round button in the bar that opens the head card again
 * as the whole menu: the sections as large rows, then the page's one call to action with the way
 * in beside it. The language picker sits by the close button. Tapping any link inside closes it
 * and leaves focus where the link took the visitor, not back on the button.
 *
 * The panel renders in a portal, outside the page wrapper, so `style` carries the page's tints.
 */
export const LandingMenu = ({
  brand,
  links,
  cta,
  style,
  className,
}: {
  brand: ReactNode;
  links: LandingMenuLink[];
  /** The bar's main button, repeated under the links. Sign-in sits beside it on its own. */
  cta: ReactNode;
  style?: CSSProperties;
  /** The trigger, so it can match the buttons beside it in the bar. */
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const followedLink = useRef(false);

  const closeOnLink = (event: MouseEvent) => {
    if (!(event.target instanceof Element) || !event.target.closest("a")) return;
    followedLink.current = true;
    setOpen(false);
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Trigger aria-label={m.landing_menu_open()} className={cn(roundButton, className)}>
        <Menu aria-hidden className="size-[18px]" />
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/25 duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          style={style}
          onClick={closeOnLink}
          onCloseAutoFocus={(event) => {
            if (!followedLink.current) return;
            followedLink.current = false;
            event.preventDefault();
          }}
          className="fixed inset-x-3 top-3 z-50 flex max-h-[calc(100svh-24px)] origin-top flex-col overflow-y-auto overscroll-contain rounded-[32px] border border-border bg-background p-5 text-sm text-foreground shadow-[0_24px_60px_rgb(0_0_0/0.18)] outline-none duration-200 ease-out data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-top-2"
        >
          <DialogPrimitive.Title className="sr-only">{m.landing_menu_title()}</DialogPrimitive.Title>
          <div className="flex items-center gap-2">
            <div className="mr-2 min-w-0 flex-1">{brand}</div>
            <LanguageMenu style={style} />
            <DialogPrimitive.Close aria-label={m.landing_menu_close()} className={cn(roundButton, className)}>
              <X aria-hidden className="size-[18px]" />
            </DialogPrimitive.Close>
          </div>
          <nav className="mt-6 flex flex-col">
            {links.map((link) => (
              <Link
                key={`${link.to}#${link.hash ?? ""}`}
                to={link.to}
                hash={link.hash}
                className="group flex items-center justify-between gap-4 border-b border-border py-4 text-[28px] leading-none font-medium tracking-[-0.04em] outline-none focus-visible:text-muted-foreground"
              >
                {link.label}
                <ArrowUpRight
                  aria-hidden
                  className="size-5 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:rotate-45"
                />
              </Link>
            ))}
          </nav>
          <div className="mt-6 flex items-center justify-between gap-2">
            {cta}
            <Link
              to="/login"
              className="inline-flex h-10 shrink-0 items-center rounded-full px-3 text-sm font-medium text-muted-foreground outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              {m.landing_nav_sign_in()}
            </Link>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
